import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, X, Sprout, Bell, BellRing, Check } from 'lucide-react';
import axios from 'axios';
import { cropImage } from '@/lib/cropImage';

// "View Calendar" overlay for the Planner — warm cream glassmorphism matching the
// Today screen. Month + Year views, and a per-crop "Remind me" picker that saves a
// planting-day push reminder. Receives `plantings`; manages its own reminder state.
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const MSHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const PRESETS = [
  { label: '2 months before', days: 60 },
  { label: '1 month before', days: 30 },
  { label: '2 weeks before', days: 14 },
  { label: '1 week before', days: 7 },
];

const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const fmtFull = (d) =>
  d.toLocaleDateString('en-NZ', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

export default function PlannerCalendar({ plantings = [], onClose }) {
  const [mode, setMode] = useState('month'); // 'month' | 'year'
  const [reminders, setReminders] = useState(() => {
    const m = {};
    plantings.forEach((p) => { if (p.reminder) m[p.planting_id] = p.reminder; });
    return m;
  });
  const [picked, setPicked] = useState(null);     // full planting object
  const [showPicker, setShowPicker] = useState(false);
  const [custom, setCustom] = useState('');
  const [saving, setSaving] = useState(false);

  const dated = useMemo(
    () => plantings.filter((p) => p.sowing_date).map((p) => ({ ...p, _d: new Date(p.sowing_date) })),
    [plantings]
  );

  const initial = useMemo(() => {
    const now = new Date();
    if (!dated.length) return { y: now.getFullYear(), m: now.getMonth() };
    const nearest = dated.reduce((best, p) =>
      Math.abs(p._d - now) < Math.abs(best._d - now) ? p : best, dated[0]);
    return { y: nearest._d.getFullYear(), m: nearest._d.getMonth() };
  }, [dated]);

  const [cursor, setCursor] = useState(initial);

  const byDay = useMemo(() => {
    const map = {};
    dated.forEach((p) => { (map[dayKey(p._d)] ||= []).push(p); });
    return map;
  }, [dated]);

  // plantings grouped by month for the chosen year (Year view)
  const byMonth = useMemo(() => {
    const map = Array.from({ length: 12 }, () => []);
    dated.forEach((p) => { if (p._d.getFullYear() === cursor.y) map[p._d.getMonth()].push(p); });
    return map;
  }, [dated, cursor.y]);

  const cells = useMemo(() => {
    const first = new Date(cursor.y, cursor.m, 1);
    const offset = (first.getDay() + 6) % 7; // Monday-start
    const count = new Date(cursor.y, cursor.m + 1, 0).getDate();
    const prevCount = new Date(cursor.y, cursor.m, 0).getDate();
    const arr = [];
    for (let i = offset - 1; i >= 0; i--) arr.push({ day: prevCount - i, out: true });
    for (let d = 1; d <= count; d++) arr.push({ day: d, out: false });
    while (arr.length % 7 !== 0) arr.push({ day: arr.length - (offset + count) + 1, out: true });
    return arr;
  }, [cursor]);

  const today = new Date();
  const isToday = (day) =>
    day === today.getDate() && cursor.m === today.getMonth() && cursor.y === today.getFullYear();

  const move = (dir) => {
    if (mode === 'year') { setCursor((c) => ({ ...c, y: c.y + dir })); return; }
    setCursor((c) => {
      const m = c.m + dir;
      if (m < 0) return { y: c.y - 1, m: 11 };
      if (m > 11) return { y: c.y + 1, m: 0 };
      return { y: c.y, m };
    });
  };

  const openPicked = (p) => { setPicked(p); setShowPicker(false); setCustom(''); };

  const saveReminder = async (days) => {
    if (!picked || !days || saving) return;
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/plantings/${picked.planting_id}/reminder`, { offset_days: Number(days) }, { withCredentials: true });
      setReminders((m) => ({ ...m, [picked.planting_id]: data.reminder }));
      setShowPicker(false);
    } catch (e) {
      // non-blocking — leave picker open so the user can retry
    } finally {
      setSaving(false);
    }
  };

  const removeReminder = async () => {
    if (!picked || saving) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/plantings/${picked.planting_id}/reminder`, { withCredentials: true });
      setReminders((m) => { const n = { ...m }; delete n[picked.planting_id]; return n; });
      setShowPicker(false);
    } catch (e) { /* non-blocking */ } finally { setSaving(false); }
  };

  const pickedReminder = picked ? reminders[picked.planting_id] : null;

  const GLASS = {
    background: 'rgba(250,246,236,0.86)',
    backdropFilter: 'blur(24px) saturate(1.2)',
    WebkitBackdropFilter: 'blur(24px) saturate(1.2)',
    border: '1px solid rgba(255,255,255,0.6)',
    boxShadow: '0 40px 90px rgba(15,45,31,0.28), inset 0 1px 0 rgba(255,255,255,0.7)',
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[70] flex items-center justify-center p-4"
        style={{ background: 'rgba(15,45,31,0.34)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}
        onClick={onClose} data-testid="planner-calendar-overlay"
      >
        <motion.div
          className="relative w-full max-w-md overflow-hidden"
          style={{ borderRadius: 30, padding: '22px 20px 18px', ...GLASS }}
          initial={{ opacity: 0, y: 26, scale: 0.965 }} animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.97 }} transition={{ duration: 0.45, ease: [0.2, 0.9, 0.25, 1] }}
          onClick={(e) => e.stopPropagation()} data-testid="planner-calendar-sheet"
        >
          <div className="absolute inset-x-0 top-0 h-32 pointer-events-none"
            style={{ background: 'radial-gradient(420px 150px at 50% -8%, rgba(201,168,91,0.16), transparent 70%)' }} />

          {/* header */}
          <div className="relative flex items-center justify-between">
            <div>
              <h3 className="text-[19px] font-extrabold tracking-tight" style={{ color: '#1A4331', fontFamily: 'Outfit, sans-serif' }}>
                {mode === 'month' ? <>{MONTHS[cursor.m]} <span style={{ color: '#C9A85B' }}>{cursor.y}</span></> : <span style={{ color: '#C9A85B' }}>{cursor.y}</span>}
              </h3>
              <p className="text-[11px] font-semibold mt-0.5" style={{ color: '#7A6B57', letterSpacing: '0.02em' }}>
                YOUR PLANNED SOWINGS
              </p>
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={() => move(-1)} data-testid="calendar-prev-month"
                className="w-8 h-8 rounded-full flex items-center justify-center transition-colors"
                style={{ background: 'rgba(26,67,49,0.06)', border: '1px solid rgba(26,67,49,0.12)' }}>
                <ChevronLeft className="w-[15px] h-[15px]" style={{ color: '#1A4331' }} />
              </button>
              <button onClick={() => move(1)} data-testid="calendar-next-month"
                className="w-8 h-8 rounded-full flex items-center justify-center transition-colors"
                style={{ background: 'rgba(26,67,49,0.06)', border: '1px solid rgba(26,67,49,0.12)' }}>
                <ChevronRight className="w-[15px] h-[15px]" style={{ color: '#1A4331' }} />
              </button>
              <button onClick={onClose} data-testid="calendar-close"
                className="w-[34px] h-[34px] rounded-full flex items-center justify-center ml-1 transition-colors"
                style={{ background: 'rgba(26,67,49,0.08)', border: '1px solid rgba(201,168,91,0.4)' }}>
                <X className="w-4 h-4" style={{ color: '#1A4331' }} />
              </button>
            </div>
          </div>

          {/* month / year toggle */}
          <div className="relative mt-3.5 flex p-1 rounded-full" style={{ background: 'rgba(26,67,49,0.07)', border: '1px solid rgba(26,67,49,0.08)' }}>
            {['month', 'year'].map((m) => (
              <button key={m} onClick={() => setMode(m)} data-testid={`calendar-view-toggle-${m}`}
                className="flex-1 py-1.5 rounded-full text-[12.5px] font-bold capitalize transition-all"
                style={mode === m
                  ? { background: '#1A4331', color: '#FAF6EC', boxShadow: '0 3px 10px rgba(15,45,31,0.25)' }
                  : { background: 'transparent', color: '#7A6B57' }}>
                {m} view
              </button>
            ))}
          </div>

          {mode === 'month' ? (
            <>
              {/* weekday row */}
              <div className="relative grid grid-cols-7 gap-1.5 mt-3.5">
                {DOW.map((d) => (
                  <span key={d} className="text-center text-[10px] font-bold uppercase" style={{ color: '#8A9284', letterSpacing: '0.12em', fontFamily: 'Outfit, sans-serif' }}>{d}</span>
                ))}
              </div>
              {/* grid */}
              <div className="relative grid grid-cols-7 gap-1.5 mt-1.5">
                {cells.map((cell, i) => {
                  const evs = cell.out ? [] : (byDay[`${cursor.y}-${cursor.m}-${cell.day}`] || []);
                  const todayCell = !cell.out && isToday(cell.day);
                  return (
                    <div key={i}
                      className="rounded-[13px] px-1 pt-1.5 pb-1 flex flex-col gap-1 overflow-hidden"
                      style={{
                        minHeight: 78, opacity: cell.out ? 0.3 : 1,
                        background: todayCell ? 'rgba(201,168,91,0.14)' : 'rgba(26,67,49,0.045)',
                        border: todayCell ? '1.5px solid #C9A85B' : '1px solid rgba(26,67,49,0.08)',
                      }}
                      data-testid={cell.out ? undefined : `calendar-day-${cell.day}`}
                    >
                      <span className="text-[12px] font-bold leading-none pl-0.5" style={{ color: todayCell ? '#9A7B2E' : '#3E4A3B', fontFamily: 'Outfit, sans-serif' }}>{cell.day}</span>
                      {evs.slice(0, 2).map((p) => (
                        <button key={p.planting_id} onClick={() => openPicked(p)} data-testid={`calendar-crop-${p.planting_id}`}
                          className="flex items-center gap-1 px-1 py-[2.5px] rounded-[7px] transition-transform hover:-translate-y-px w-full"
                          style={{ background: 'rgba(255,255,255,0.72)', border: '1px solid rgba(26,67,49,0.1)' }}>
                          <img src={cropImage(p.crop_name, p.category, p.image_url)} alt="" loading="lazy" className="rounded-full flex-none object-cover" style={{ width: 13, height: 13, border: '1px solid rgba(26,67,49,0.12)' }} />
                          <b className="text-[8.5px] font-bold truncate min-w-0 flex-1 text-left" style={{ color: '#1A4331' }}>{p.crop_name}</b>
                          {reminders[p.planting_id] && <BellRing className="w-2.5 h-2.5 flex-none" style={{ color: '#C9A85B' }} />}
                        </button>
                      ))}
                      {evs.length > 2 && (
                        <span className="text-[8.5px] font-bold pl-1" style={{ color: '#9A7B2E' }}>+{evs.length - 2}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            /* ------- YEAR VIEW ------- */
            <div className="relative grid grid-cols-3 gap-2 mt-3.5">
              {MONTHS.map((mName, mi) => {
                const evs = byMonth[mi];
                const isNow = mi === today.getMonth() && cursor.y === today.getFullYear();
                return (
                  <button key={mi} onClick={() => { setCursor((c) => ({ ...c, m: mi })); setMode('month'); }}
                    data-testid={`calendar-year-month-${mi}`}
                    className="rounded-[14px] p-2 flex flex-col transition-transform hover:-translate-y-0.5 text-left"
                    style={{
                      minHeight: 72,
                      background: isNow ? 'rgba(201,168,91,0.14)' : 'rgba(26,67,49,0.045)',
                      border: isNow ? '1.5px solid #C9A85B' : '1px solid rgba(26,67,49,0.08)',
                    }}>
                    <div className="flex items-center justify-between">
                      <span className="text-[12px] font-extrabold" style={{ color: isNow ? '#9A7B2E' : '#1A4331', fontFamily: 'Outfit, sans-serif' }}>{MSHORT[mi]}</span>
                      {evs.length > 0 && (
                        <span className="text-[10px] font-bold px-1.5 rounded-full" style={{ color: '#FAF6EC', background: '#1A4331' }} data-testid={`year-month-count-${mi}`}>{evs.length}</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-auto pt-2">
                      {evs.slice(0, 7).map((p) => (
                        <img key={p.planting_id} src={cropImage(p.crop_name, p.category, p.image_url)} alt="" loading="lazy" title={p.crop_name}
                          className="rounded-full object-cover" style={{ width: 13, height: 13, border: '1px solid rgba(26,67,49,0.12)' }} />
                      ))}
                      {evs.length === 0 && <span className="text-[9px] font-semibold" style={{ color: '#A7AFA2' }}>—</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* legend */}
          <div className="relative flex items-center justify-between mt-3.5 pt-3" style={{ borderTop: '1px solid rgba(26,67,49,0.1)' }}>
            <span className="text-[11px] font-semibold flex items-center gap-1.5" style={{ color: '#5C665C' }}>
              <i className="rounded-full" style={{ width: 7, height: 7, background: '#6B9F4A' }} />
              {dated.length} crop{dated.length === 1 ? '' : 's'} planned
            </span>
            <span className="text-[11px] font-bold" style={{ color: '#9A7B2E' }}>Tap a crop for details</span>
          </div>

          {/* crop summary popup */}
          <AnimatePresence>
            {picked && (
              <motion.div className="absolute inset-0 flex items-center justify-center z-10" style={{ borderRadius: 30 }}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}
                onClick={() => setPicked(null)} data-testid="calendar-crop-popup">
                <div className="absolute inset-0" style={{ background: 'rgba(15,45,31,0.26)', backdropFilter: 'blur(3px)', borderRadius: 30 }} />
                <motion.div className="relative w-[300px]"
                  style={{
                    borderRadius: 22, padding: 20,
                    background: 'rgba(250,246,236,0.96)',
                    border: '1px solid rgba(255,255,255,0.7)',
                    boxShadow: '0 30px 70px rgba(15,45,31,0.3), inset 0 1px 0 rgba(255,255,255,0.8)',
                  }}
                  initial={{ scale: 0.94 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} transition={{ duration: 0.3, ease: [0.2, 0.9, 0.25, 1] }}
                  onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => setPicked(null)} data-testid="calendar-popup-close"
                    className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(26,67,49,0.07)', border: '1px solid rgba(201,168,91,0.4)' }}>
                    <X className="w-3.5 h-3.5" style={{ color: '#1A4331' }} />
                  </button>
                  <div className="flex items-center gap-3">
                    <div className="w-[50px] h-[50px] rounded-2xl flex items-center justify-center flex-none" style={{ background: 'rgba(107,159,74,0.16)', border: '1px solid rgba(107,159,74,0.3)' }}>
                      <Sprout className="w-6 h-6" style={{ color: '#4E7A33' }} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[19px] font-extrabold leading-tight truncate" style={{ color: '#1A4331', fontFamily: 'Outfit, sans-serif' }} data-testid="popup-crop-name">{picked.crop_name}</div>
                      <div className="text-[11px] font-semibold uppercase mt-0.5" style={{ color: '#7A6B57', letterSpacing: '0.08em' }}>Planned sowing</div>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-2.5 rounded-xl px-3 py-2.5" style={{ background: 'rgba(26,67,49,0.05)', border: '1px solid rgba(26,67,49,0.08)' }}>
                    <CalendarIcon className="w-[15px] h-[15px] flex-none" style={{ color: '#C9A85B' }} />
                    <span className="text-[11px] font-semibold" style={{ color: '#7A6B57' }}>Planting date</span>
                    <span className="text-[13px] font-bold ml-auto" style={{ color: '#1A4331' }} data-testid="popup-crop-date">{picked._d ? fmtFull(picked._d) : fmtFull(new Date(picked.sowing_date))}</span>
                  </div>

                  {/* reminder row / picker */}
                  {!showPicker ? (
                    pickedReminder ? (
                      <div className="mt-2.5 flex items-center gap-2.5 rounded-xl px-3 py-2.5" style={{ background: 'rgba(201,168,91,0.14)', border: '1px solid rgba(201,168,91,0.35)' }} data-testid="reminder-status">
                        <BellRing className="w-[15px] h-[15px] flex-none" style={{ color: '#9A7B2E' }} />
                        <span className="text-[12px] font-bold" style={{ color: '#1A4331' }}>Reminder · {pickedReminder.label} before</span>
                        <button onClick={() => setShowPicker(true)} data-testid="reminder-change-btn" className="ml-auto text-[11px] font-bold" style={{ color: '#4E7A33' }}>Change</button>
                      </div>
                    ) : (
                      <button onClick={() => setShowPicker(true)} data-testid="calendar-remind-btn"
                        className="mt-2.5 w-full flex items-center justify-center gap-2 rounded-xl py-2.5 transition-transform hover:-translate-y-px"
                        style={{ background: '#1A4331', boxShadow: '0 6px 16px rgba(15,45,31,0.25)' }}>
                        <Bell className="w-[15px] h-[15px]" style={{ color: '#FAF6EC' }} />
                        <b className="text-[13px] font-bold" style={{ color: '#FAF6EC' }}>Remind me</b>
                      </button>
                    )
                  ) : (
                    <div className="mt-2.5" data-testid="calendar-reminder-picker">
                      <p className="text-[11px] font-bold uppercase mb-1.5" style={{ color: '#7A6B57', letterSpacing: '0.06em' }}>Remind me…</p>
                      <div className="flex flex-col gap-1.5">
                        {PRESETS.map((opt) => (
                          <button key={opt.days} onClick={() => saveReminder(opt.days)} disabled={saving} data-testid={`reminder-option-${opt.days}`}
                            className="flex items-center justify-between rounded-xl px-3 py-2.5 transition-colors"
                            style={{ background: pickedReminder?.offset_days === opt.days ? 'rgba(107,159,74,0.16)' : 'rgba(26,67,49,0.05)', border: '1px solid rgba(26,67,49,0.08)' }}>
                            <span className="text-[13px] font-bold" style={{ color: '#1A4331' }}>{opt.label}</span>
                            {pickedReminder?.offset_days === opt.days && <Check className="w-4 h-4" style={{ color: '#4E7A33' }} />}
                          </button>
                        ))}
                        <div className="flex items-center gap-2 mt-0.5">
                          <input type="number" min="1" max="365" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Custom days"
                            data-testid="reminder-custom-input"
                            className="flex-1 rounded-xl px-3 py-2.5 text-[13px] font-semibold outline-none"
                            style={{ background: 'rgba(26,67,49,0.05)', border: '1px solid rgba(26,67,49,0.12)', color: '#1A4331' }} />
                          <button onClick={() => saveReminder(custom)} disabled={saving || !custom} data-testid="reminder-custom-save"
                            className="rounded-xl px-3.5 py-2.5 text-[12px] font-bold" style={{ background: '#1A4331', color: '#FAF6EC', opacity: (saving || !custom) ? 0.5 : 1 }}>
                            Save
                          </button>
                        </div>
                        {pickedReminder && (
                          <button onClick={removeReminder} disabled={saving} data-testid="reminder-remove-btn"
                            className="mt-0.5 text-[12px] font-bold py-2" style={{ color: '#C66B4A' }}>
                            Remove reminder
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
